import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { BeatLoader } from "react-spinners";
import ErrorMessage from "../../components/ui/ErrorMessage";
import {
	useModalEntryType,
	useModalNavigation,
	useModalPicker,
	useModalSearch,
} from "../../context/ModalControllerContext";
import { useEditableMeal } from "../../context/ModalEntryContext";
import { classNames, normalizeMealItems, resolveEffectiveGrams } from "../../helpers";
import useIsOverflowing from "../../hooks/ui/useIsOverflowing";
import DatabaseToggle from "../components/DatabaseToggle";
import ModalHeader from "../components/ModalHeader";
import ModalMenu from "../components/ModalMenu";
import DiarySvg from "../icons/DiarySvg";
import BarcodeScanner from "../components/BarcodeScanner";
import useBarcodeSearch from "../../hooks/data/barcode/useBarcodeSearch";

const INITIAL_VISIBLE_ITEMS = 10;
const DB_COLORS = {
	db1: "#FFDB4C",
	db2: "#AF7DEF",
};

const ModalDefault = () => {
	const { entryType, setEntryType } = useModalEntryType();
	const { addEditableItems, createFromFavorite, createFromRecipe } = useEditableMeal();

	const [showScanner, setShowScanner] = useState(false);

	const search = useModalSearch();

	const barcodeSearch = useBarcodeSearch();

	const { setView, close, canGoBack, goBack } = useModalNavigation();
	const { pickerMode, onPickItems } = useModalPicker();

	const searchInputRef = useRef(null);

	// DERIVED

	const categories = useMemo(() => {

		const base = [
			{
				title: "Tidigare använda livsmedel",
				data: search.user_items.data,
				showPortion: true,
                paginated: true,
                paginationDetails: {...search.user_items.paginationDetails, type: 'user_items'}
			},
			{
				title: "Livsmedel",
				data: search.items.data,
				showPortion: true,
                paginated: true,
                paginationDetails: {...search.items.paginationDetails, type: 'items'}
			},
			{
				title: "Mina recept",
				data: search.user_recipes.data,
				showPortion: false,
				isRecipe: true,
                paginated: true,
                paginationDetails: {...search.user_recipes.paginationDetails, type: 'user_recipes'}
			},
			{
				title: "Recept",
				data: search.recipes.data,
				showPortion: false,
				isRecipe: true,
                paginated: true,
                paginationDetails: {...search.recipes.paginationDetails, type: 'recipes'}
			},
		];

		if (!pickerMode) {
			base.unshift({
				title: "Mina Favoritmåltider",
				data: search.favorite.data,
				showPortion: false,
				isFavorite: true,
                paginated: true,
                paginationDetails: {...search.favorite.paginationDetails, type: 'favorite'}
			});
		}

		return base;
	}, [pickerMode, search]);

	const visibleCategories = useMemo(() => {
		return categories
			.map(({ title, data, showPortion, isFavorite, isRecipe, paginated, paginationDetails }) => {

				if (!data?.length) return null;

				const isExpanded = !!search.expandedCategories[title];

				return {
					title,
					showPortion,
					data,
					visibleItems: paginated ? data : (isExpanded ? data : data.slice(0, INITIAL_VISIBLE_ITEMS)),
					isExpanded,
					isFavorite,
					isRecipe,
                    paginated,
                    paginationDetails: paginationDetails ?? {}
				};
			})
			.filter(Boolean);
	}, [categories, search.expandedCategories]);

	const hasResults = visibleCategories.length > 0;

	const templateIds = useMemo(() => {
		const templates = visibleCategories.filter((c) => c.isFavorite || c.isRecipe).flatMap((c) => c.data);

		return templates.map((i) => i.id);
	}, [visibleCategories]);

	const isProcessing = search.isLoading || barcodeSearch.isPending;
	const isError = search.isError || barcodeSearch.isError;
	const error = search.error || barcodeSearch.error;

	// HANDLERS

	const handleAddMeal = (item, isFavorite, isRecipe) => {
		const normalized = normalizeMealItems(item);

		if (pickerMode) {
			onPickItems?.(normalized);
			close();
			return;
		}

		if (isRecipe) {
			createFromRecipe(item);

			setView("recipeServings");
			return;
		}

		if (isFavorite) {
			createFromFavorite(item);
			setView("editMeal");
			return;
		}

		addEditableItems(normalized);
		setView("editMeal");
	};

	const handleViewNutrition = (item, isFavorite, isRecipe) => {
		if (isFavorite || isRecipe) {
			setView("nutrition", {
				...item,
				isFavorite,
				isRecipe,
			});

			return;
		}

		setView("itemNutrition", {
			...item,
			_isPerGramNutrition: true,
		});
	};

	const handleBack = () => {
		if (search.searchMode) {
			search.onResetSearch();
			return;
		}

		goBack();
	};

	// BARCODE

	const resolveBarcodeItem = async (ean) => {
		try {
			const data = await barcodeSearch.mutateAsync({ ean });

			const item = data?.items?.[0];
			if (!item) return null;

			return item;
		} catch (err) {
			console.warn(err);
			return false;
		}
	};

	const editScannedItem = (item) => {
		setShowScanner(false);
		addEditableItems(normalizeMealItems(item));
		setView("editMeal");
	};

	// UI

    const handleLoadMore = (data) => {
        search.onLoadMore(data)
    }

	const handleExpandCategory = (title) => {
		search.onExpandCategory(title);
		requestAnimationFrame(() => checkOverflow());
	};

	const [openItems, setOpenItems] = useState({});

	const toggleTemplate = (id) => {
		setOpenItems((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	};

	useEffect(() => {
		const next = templateIds.reduce((acc, id) => {
			acc[id] = false;
			return acc;
		}, {});

		setOpenItems(next);
	}, [templateIds]);

	const {
		ref: resultsRef,
		isOverflowing,
		checkOverflow,
	} = useIsOverflowing([
		search.favorite.data.length,
		search.user_items.data.length,
		search.items.data.length,
		search.recipes.data.length,
	]);

	return (
		<>
			<ModalHeader
				title={search.searchMode ? "Sök måltid" : "Lägg till måltid"}
				onClose={close}
				onGoBack={handleBack}
				showBack={search.searchMode || canGoBack}
			/>

			<div className={`modal-body default ${hasResults ? "has-results" : ""}`}>
				{!isError ? (
					<>
						<AnimatePresence mode="wait">
							{!search.searchMode && !pickerMode && (
								<motion.div
									key="menu"
									exit={{ opacity: 0, height: 0 }}
									transition={{ duration: 0.25 }}
								>
									<ModalMenu
										activeMealType={entryType}
										onSelect={(mealType) => setEntryType(mealType)}
									/>
								</motion.div>
							)}
						</AnimatePresence>

						{/* --- SEARCH FIELD --- */}
						<motion.div
							className="input-wrapper"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.15 }}
							onAnimationComplete={() => {
								searchInputRef.current?.focus();
							}}
						>
							<input
								id="modal-search"
								ref={searchInputRef}
								type="text"
								className="modal-input search-input input-field"
								placeholder="Livsmedel, måltid, recept"
								value={search.searchInput}
								onChange={search.onSearchChange}
							/>

							<img id="search-icon" src="/assets/images/diary-icons/search.svg" alt="" />

							{isProcessing && (
								<div className="search-loading-spinner">
									<BeatLoader color="#ef61a7" size={6} />
								</div>
							)}

							{!isProcessing && search.searchInput && (
								<button className="reset-search-btn" onClick={search.onClearSearchInput}>
									<img src="/assets/images/diary-icons/times-pink-larger.svg" alt="" />
								</button>
							)}
						</motion.div>

						<DatabaseToggle values={search.checkboxes} setValues={search.onSelectCheckboxes} />

						{/* --- SEARCH RESULTS --- */}
						{search.searchMode && (
							<div
								ref={resultsRef}
								className={`modal-search-results ${isOverflowing ? "has-scroll" : ""}`}
							>
								{search.showNoResults && (
									<motion.div
										className="no-results"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{ duration: 0.2 }}
									>
										<p>Inga sökresultat hittades.</p>
									</motion.div>
								)}

								{visibleCategories.map(
									({
										title,
										showPortion,
										data,
										visibleItems,
										isExpanded,
										isFavorite,
										isRecipe,
                                        paginated,
                                        paginationDetails
									}) => {
										return (
											<div key={title} className="search-result-category">
												<h4>{title}:</h4>

												{visibleItems.map((item) => {
													const isOpen = openItems[item.id] || false;

													const { database: db } = item;

													const isExternal = db === "db1" || db === "db2";
													const color = DB_COLORS[db] ?? null;

													const isTemplate = isFavorite || isRecipe;

													return (
														<motion.div
															key={item.id}
															className={classNames("search-result-item", db, {
																"is-template": isTemplate,
															})}
															initial={{ opacity: 0 }}
															animate={{ opacity: 1 }}
															transition={{ duration: 0.15 }}
															onClick={() =>
																handleAddMeal(item, isFavorite, isRecipe)
															}
														>
															{isExternal && color && (
																<DiarySvg
																	name="circle"
																	color={[color, "#A77E52"]}
																/>
															)}

															<span
																className={classNames("item-name", {
																	"is-template-name": isTemplate,
																	"show-portion": showPortion,
																	"is-open": isOpen,
																})}
																onClick={(e) => {
																	if (!isTemplate) return;

																	e.stopPropagation();
																	toggleTemplate(item.id);
																}}
															>
																{isFavorite && (
																	<img
																		src="/assets/images/diary-icons/heart-gray-secondary.svg"
																		alt=""
																	/>
																)}
																{isRecipe && (
																	<img
																		src="/assets/images/diary-icons/book-open.svg"
																		alt=""
																	/>
																)}

																{item.name}{isRecipe && item.kcal && (
                                                                    <span>({item.kcal} kcal)</span>
																)}

																{isTemplate && <DiarySvg name="chevron" />}{" "}
																{showPortion && (
																	<span className="portion-text">
																		({resolveEffectiveGrams(item)}{" "}
																		g/portion)
																	</span>
																)}
															</span>

															<div className="search-result-item-buttons">
																<button
																	className="view-nutrition-btn"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleViewNutrition(
																			item,
																			isFavorite,
																			isRecipe,
																		);
																	}}
																>
																	<DiarySvg name="chart" />
																</button>

																<button
																	className="add-item-btn"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleAddMeal(
																			item,
																			isFavorite,
																			isRecipe,
																		);
																	}}
																>
																	<DiarySvg name="plus" />
																</button>
															</div>
															<AnimatePresence initial={false}>
																{isOpen && (
																	<motion.div
																		key={`expanded-${item.id}`}
																		className="expanded-content"
																		initial={{ opacity: 0, height: 0 }}
																		animate={{
																			opacity: 1,
																			height: "auto",
																		}}
																		exit={{ opacity: 0, height: 0 }}
																		transition={{ duration: 0.15 }}
																		layout
																	>
																		<ul>
																			{item?.items.map((sub) => (
																				<li key={`sub-${sub?.id}`}>
																					- {sub?.name}
																				</li>
																			))}
																		</ul>
																	</motion.div>
																)}
															</AnimatePresence>
														</motion.div>
													);
												})}

												{!paginated && data.length > INITIAL_VISIBLE_ITEMS && !isExpanded && (
													<button
														className="show-more-btn border-btn-green"
														onClick={() => handleExpandCategory(title)}
													>
														Visa fler...
													</button>
												)}

                                                {paginated && paginationDetails?.current_page !== paginationDetails?.last_page && (

													<button
														className="show-more-btn border-btn-green"
                                                        onClick={() => handleLoadMore(paginationDetails)}
													>
														Visa fler...
													</button>
                                                )}


											</div>
										);
									},
								)}
							</div>
						)}
					</>
				) : (
					search.searchMode && (
						<ErrorMessage
							message={error?.message || "Ett fel uppstod vid sökningen. Försök igen."}
						/>
					)
				)}
			</div>

			{showScanner && (
				<BarcodeScanner
					onDetected={resolveBarcodeItem}
					onClose={() => setShowScanner(false)}
					onSuccess={editScannedItem}
				/>
			)}

			{!search.searchMode && !pickerMode && (
				<div className="modal-footer default">
					{/* <button className="btn-green camera-btn" onClick={() => setShowScanner(true)}>
						<DiarySvg name="camera" />
						<span>Skanna streckkod</span>
					</button> */}
					<button
						type="button"
						className="marked-border-btn bold-title"
						onClick={() => setView("ownMeal")}
					>
						Lägg till egen kaloriberäknad måltid
					</button>
				</div>
			)}
		</>
	);
};

export default ModalDefault;
